import {useEffect,useRef,useState} from 'react';
import {API} from '../lib/api';
import {assignmentStamp,draftKey,readDraft,writeDraft,removeDraft,holdDraftLock,removeDraftSnapshot} from '../lib/handoverDraft';
import {initialHandover} from '../lib/handover';
import {randomId} from '../lib/randomId';

export default function useHandoverDraft({userId,tenantId,assignment,minimum,mode,context={}}) {
  const key=draftKey(API,userId,assignment.id,mode,tenantId),stamp=assignmentStamp(assignment,context);
  const [initial]=useState(()=>{
    let raw;
    try{
      raw=window.localStorage.getItem(key);
      return {raw,draft:readDraft(window.localStorage,key)};
    }
    catch(e){return {raw,error:e.message||'Impossibile leggere la bozza su questo browser.'};}
  });
  const [form,setForm]=useState(()=>({...initialHandover(assignment,minimum,mode,context),...initial.draft?.form}));
  const [step,setStep]=useState(initial.draft?.step||0);
  const [savedAt,setSavedAt]=useState(initial.draft?.savedAt||'');
  const [saveError,setSaveError]=useState(initial.error||'');
  const [conflict,setConflict]=useState(initial.draft&&initial.draft.stamp!==stamp?'Il noleggio è cambiato dopo il salvataggio della bozza. Controlla i dati aggiornati prima di ricominciare.':'');
  const initialStamp=useRef(stamp);
  useEffect(()=>{if(initialStamp.current!==stamp)setConflict('L’incarico o il noleggio è cambiato. Esci e riapri i dati aggiornati; questa bozza non verrà confermata.');},[stamp]);
  const revision=useRef(initial.draft?.revision??null);
  const storedRaw=useRef(initial.raw);
  const [lockReady,setLockReady]=useState(false),[lockError,setLockError]=useState('');
  const lockAvailable=useRef(false);
  const data=useRef({form,step});
  const finished=useRef(false);
  const persist=(next)=>{
    if(finished.current||!lockAvailable.current||conflict)return false;
    data.current=next;setForm(next.form);setStep(next.step);
    try{
      const stored=writeDraft(window.localStorage,key,{...next,stamp},revision.current);
      revision.current=stored.revision;storedRaw.current=JSON.stringify(stored);setSavedAt(stored.savedAt);setSaveError('');return true;
    }catch(e){setSaveError(e.message||'Salvataggio non riuscito: spazio del browser non disponibile.');return false;}
  };
  useEffect(()=>{
    const release=holdDraftLock(key,error=>{lockAvailable.current=!error;setLockReady(!error);setLockError(error);});
    return ()=>{lockAvailable.current=false;release();};
  },[key]);
  useEffect(()=>{
    const external=e=>{if(e.storageArea===window.localStorage&&(e.key===key||e.key===null))setConflict('La bozza è cambiata in un’altra scheda. Ricarica per riprendere la versione salvata.');};
    window.addEventListener('storage',external);return()=>window.removeEventListener('storage',external);
  },[key]);
  const change=(field,value)=>persist({...data.current,form:{...data.current.form,[field]:value}});
  const changeMany=patch=>persist({...data.current,form:{...data.current.form,...patch}});
  const navigate=index=>persist({...data.current,step:index});
  const retry=()=>persist(data.current);
  const clear=()=>{removeDraft(window.localStorage,key,revision.current);revision.current=null;};
  const discard=()=>{
    try{
      if(!lockAvailable.current||finished.current)return false;
      removeDraftSnapshot(window.localStorage,key,storedRaw.current);
      finished.current=true;revision.current=null;setSavedAt('');setSaveError('');return true;
    }catch(e){setSaveError(e.message);return false;}
  };
  const complete=()=>{
    finished.current=true;
    try{clear();return '';}catch(e){return 'Il verbale è registrato, ma non è stato possibile rimuovere la bozza locale.';}
  };
  const canRecoverNotes=!!(context.collectAllowed&&lockReady&&conflict&&initial.draft?.form.notes?.trim());
  const recoverNotes=()=>{
    if(!canRecoverNotes||!lockAvailable.current||finished.current)return false;
    try{
      const storage=window.localStorage,sourceKey=key,sourceRaw=storedRaw.current;
      if(storage.getItem(sourceKey)!==sourceRaw)throw new Error('La bozza originale è cambiata. Esci e riaprila prima di recuperare le note.');
      const source=readDraft(storage,sourceKey),at=new Date().toISOString();
      const archiveKey=`${key}:archive:${randomId()}`;
      const archived=JSON.stringify({source_key:sourceKey,source_raw:sourceRaw,archived_at:at});
      storage.setItem(archiveKey,archived);
      if(storage.getItem(archiveKey)!==archived)throw new Error('La copia originale non è stata conservata. Recupero annullato.');
      if(storage.getItem(sourceKey)!==sourceRaw)throw new Error('La bozza originale è cambiata durante il recupero. Non è stata sostituita.');
      const fresh={...initialHandover(assignment,minimum,mode,context),photos:[],notes:source.form.notes,recovered_notes:{source_saved_at:source.savedAt||null,recovered_at:at,archive_key:archiveKey}};
      const saved=writeDraft(storage,key,{form:fresh,step:0,stamp},revision.current);
      revision.current=saved.revision;storedRaw.current=JSON.stringify(saved);initialStamp.current=stamp;data.current={form:fresh,step:0};
      setForm(fresh);setStep(0);setSavedAt(saved.savedAt);setSaveError('');setConflict('');
      return true;
    }catch(e){setSaveError(e.message||'Impossibile conservare la bozza originale. Nessun recupero eseguito.');return false;}
  };
  return {form,step,change,changeMany,navigate,savedAt,saveError,conflict,canRecoverNotes,recoverNotes,lockReady,lockError,recovered:!!initial.draft,retry,discard,complete,finished};
}
